import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} UpdateSubSchoolData
 * @property {string} [name] - School name
 * @property {string} [email] - School email
 * @property {string} [phone] - Phone number (optional)
 * @property {string} [address] - Address (optional)
 * @property {File} [image] - School image file (optional)
 */

/**
 * @typedef {Object} UpdateSubSchoolResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {Object} [data] - Updated sub-school data
 */

/**
 * Custom hook to update a sub-school under an organization
 */
export const useUpdateSubSchool = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * Updates a sub-school under an organization
   * @param {string|number} schoolId - The ID of the sub-school to update
   * @param {UpdateSubSchoolData} schoolData - The sub-school data to update
   * @param {(message: string) => void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<boolean>} - Returns true on success, false on failure
   */
  const updateSubSchool = async (schoolId, schoolData, onSuccess) => {
    if (!schoolId) {
      setError("School ID is required.");
      return false;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const formData = new FormData();

      // Add fields only if they are provided
      if (schoolData.name !== undefined) {
        formData.append("name", schoolData.name);
      }
      if (schoolData.email !== undefined) {
        formData.append("email", schoolData.email);
      }
      if (schoolData.phone !== undefined) {
        formData.append("phone", schoolData.phone);
      }
      if (schoolData.address !== undefined) {
        formData.append("address", schoolData.address);
      }
      if (schoolData.image) {
        formData.append("image", schoolData.image);
      }

      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/organization/admin/sub-school/update/${schoolId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setData(response.data);

      if (onSuccess) {
        onSuccess(response.data?.message || "Sub-school updated successfully");
      }

      return true;
    } catch (err) {
      let errorMessage = "";

      if (err?.response?.data?.errors) {
        const errorKey = Object.keys(err.response.data.errors)[0];
        const firstErrorMessage = err.response.data.errors[errorKey][0];

        // Map API error keys to user-friendly messages
        const errorKeyMap = {
          email: "School Email is already taken",
          phone: "Phone number is already taken",
          name: "School name is invalid",
        };

        errorMessage = errorKeyMap[errorKey] || firstErrorMessage;
      } else if (err?.response?.status === 404) {
        errorMessage = "Sub-school not found.";
      } else if (err?.response?.status === 500) {
        errorMessage = ERROR_MESSAGES.INTERNAL_SERVER;
      } else {
        errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          ERROR_MESSAGES.UNKNOWN;
      }

      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateSubSchool,
    loading,
    error,
    data,
    setError,
  };
};
